import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import axios from '../../utils/axios';
import { MP_LABEL } from './msAssortmentApi.mjs';

const PREVIEW_TIMEOUT_MS = 180000;

function pairKey(pair) {
  return String(pair.key ?? `${pair.marketplace}:${pair.account_id}:${pair.product_key}:${pair.ms_id}`);
}

function accountFilterKey(row) {
  return `${row.marketplace}:${row.account_id}`;
}

function textOr(...candidates) {
  for (const value of candidates) {
    const text = String(value == null ? '' : value).trim();
    if (text) return text;
  }
  return '—';
}

function previousForcedText(forced) {
  const list = (Array.isArray(forced) ? forced : [])
    .filter((f) => f && f.forced_stock != null && String(f.forced_stock) !== '');
  if (!list.length) return 'нет';
  return list.map((f) => `${f.binding_name || f.binding_id}: ${f.forced_stock}`).join(', ');
}

export default function MsBarcodeMatchDialog({ open, onClose, onSaved, notify }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('pairs');
  const [selected, setSelected] = useState(() => new Set());
  const [enabledMap, setEnabledMap] = useState({});
  const [accountFilter, setAccountFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const sessionRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const session = ++sessionRef.current;
    setReport(null);
    setLoadError('');
    setTab('pairs');
    setSelected(new Set());
    setEnabledMap({});
    setAccountFilter('');
    setTypeFilter('');
    setSaving(false);
    setLoading(true);
    axios.post('/ms-assortment/auto-link-by-barcode/preview', {}, { silent: true, timeout: PREVIEW_TIMEOUT_MS })
      .then(({ data }) => {
        if (sessionRef.current !== session) return;
        const pairs = Array.isArray(data?.pairs) ? data.pairs : [];
        setReport(data || {});
        setSelected(new Set(pairs.map(pairKey)));
        const nextEnabled = {};
        for (const pair of pairs) nextEnabled[pairKey(pair)] = !!pair.default_enabled;
        setEnabledMap(nextEnabled);
      })
      .catch((error) => {
        if (sessionRef.current !== session) return;
        const body = error?.response?.data;
        const message = body?.message || 'Не удалось построить предпросмотр';
        setLoadError(message);
        if (error?.response?.status === 409 && body?.code === 'lock_busy') notify.warning(message);
        else notify.error(message);
      })
      .finally(() => {
        if (sessionRef.current === session) setLoading(false);
      });
  }, [open, notify]);

  const pairs = useMemo(() => (Array.isArray(report?.pairs) ? report.pairs : []), [report]);
  const conflicts = useMemo(() => (Array.isArray(report?.conflicts) ? report.conflicts : []), [report]);
  const stats = report?.stats || {};

  const accountOptions = useMemo(() => {
    const map = new Map();
    for (const row of [...pairs, ...conflicts]) {
      const key = accountFilterKey(row);
      if (!map.has(key)) {
        map.set(key, `${MP_LABEL[row.marketplace] || row.marketplace} · ${row.account_name || row.account_id}`);
      }
    }
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [pairs, conflicts]);

  const visiblePairs = useMemo(() => pairs.filter((pair) => (!accountFilter || accountFilterKey(pair) === accountFilter)
    && (!typeFilter || pair.type === typeFilter)), [pairs, accountFilter, typeFilter]);
  const visibleConflicts = useMemo(
    () => conflicts.filter((row) => !accountFilter || accountFilterKey(row) === accountFilter),
    [conflicts, accountFilter],
  );

  const visibleSelectedCount = visiblePairs.filter((pair) => selected.has(pairKey(pair))).length;
  const allVisibleSelected = visiblePairs.length > 0 && visibleSelectedCount === visiblePairs.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const pair of visiblePairs) {
        if (allVisibleSelected) next.delete(pairKey(pair));
        else next.add(pairKey(pair));
      }
      return next;
    });
  };

  const toggleOne = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    const items = pairs
      .filter((pair) => selected.has(pairKey(pair)))
      .map((pair) => {
        const item = {
          marketplace: pair.marketplace,
          account_id: pair.account_id,
          product_key: pair.product_key,
          ms_id: pair.ms_id,
          enabled: !!enabledMap[pairKey(pair)],
        };
        if (pair.previous_ms_id) item.previous_ms_id = pair.previous_ms_id;
        return item;
      });
    if (!items.length) return;
    setSaving(true);
    try {
      const { data } = await axios.post('/ms-assortment/auto-link-by-barcode', { items }, {
        silent: true,
        timeout: PREVIEW_TIMEOUT_MS,
      });
      const created = Number(data?.created) || 0;
      const rebound = Number(data?.rebound) || 0;
      const skipped = Array.isArray(data?.skipped) ? data.skipped.length : 0;
      const text = `Создано ${created}, перенесено ${rebound}${skipped ? `, пропущено ${skipped}` : ''}`;
      if (skipped) notify.warning(text);
      else notify.success(text);
      onSaved?.();
      onClose();
    } catch (error) {
      const body = error?.response?.data;
      if (error?.response?.status === 409 && body?.code === 'lock_busy') {
        notify.warning(body.message || 'Сопоставление уже выполняется, попробуйте позже');
      } else {
        notify.error(body?.message || 'Не удалось сохранить связи');
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = selected.size;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Сопоставление по штрихкоду — предпросмотр</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Ничего не записано. Отметьте пары и нажмите «Сохранить». Новые связи по умолчанию выключены;
          перенос с архивного товара переносит трансляцию и принуд.
        </Typography>

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Сверяем ШК МойСклад с карточками МП…
            </Typography>
          </Box>
        )}
        {loadError && !loading && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

        {report && (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip label={`Новых: ${stats.created ?? 0}`} color="success" size="small" />
              <Chip label={`Переносов: ${stats.rebound ?? 0}`} color="info" size="small" />
              <Chip label={`Уже было: ${stats.already ?? 0}`} size="small" />
              <Chip
                label={`Конфликты: ${stats.conflicts ?? 0}`}
                color={(stats.conflicts || 0) ? 'warning' : 'default'}
                size="small"
              />
              <Chip label={`МС со ШК: ${stats.scanned_ms ?? 0}`} variant="outlined" size="small" />
              <Chip label={`Карточек МП: ${stats.scanned_mp ?? 0}`} variant="outlined" size="small" />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 1 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
                <Tab value="pairs" label={`Пары (${pairs.length})`} />
                <Tab value="conflicts" label={`Конфликты (${conflicts.length})`} />
              </Tabs>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Кабинет</InputLabel>
                <Select label="Кабинет" value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
                  <MenuItem value="">Все кабинеты</MenuItem>
                  {accountOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {tab === 'pairs' && (
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Тип</InputLabel>
                  <Select label="Тип" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                    <MenuItem value="">Все типы</MenuItem>
                    <MenuItem value="new">Новые</MenuItem>
                    <MenuItem value="rebind">Перенос с архива</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>

            {tab === 'pairs' && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Отмечено {selectedCount} из {pairs.length}
                  {(accountFilter || typeFilter) ? ` · показано ${visiblePairs.length}` : ''}
                </Typography>
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allVisibleSelected}
                            indeterminate={someVisibleSelected}
                            onChange={toggleAllVisible}
                            disabled={!visiblePairs.length || saving}
                          />
                        </TableCell>
                        <TableCell>МП</TableCell>
                        <TableCell>Кабинет</TableCell>
                        <TableCell>Товар МП</TableCell>
                        <TableCell>ШК</TableCell>
                        <TableCell>Товар МС</TableCell>
                        <TableCell>Тип</TableCell>
                        <TableCell>Включить трансляцию</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visiblePairs.map((pair) => {
                        const key = pairKey(pair);
                        const checked = selected.has(key);
                        return (
                          <TableRow key={key} hover selected={checked}>
                            <TableCell padding="checkbox">
                              <Checkbox size="small" checked={checked} onChange={() => toggleOne(key)} disabled={saving} />
                            </TableCell>
                            <TableCell>{MP_LABEL[pair.marketplace] || pair.marketplace}</TableCell>
                            <TableCell>{pair.account_name || pair.account_id}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{pair.mp_name || pair.product_key}</Typography>
                              <Typography variant="caption" color="text.secondary">{pair.product_key}</Typography>
                            </TableCell>
                            <TableCell>{(pair.barcodes || []).join(', ') || '—'}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{textOr(pair.ms_name, pair.ms_id)}</Typography>
                            </TableCell>
                            <TableCell>
                              {pair.type === 'rebind' ? (
                                <>
                                  <Chip label="перенос с архива" color="info" size="small" />
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Было: {textOr(pair.previous_ms_name, pair.previous_ms_id)},
                                    {' '}трансляция была {pair.previous_enabled ? 'вкл' : 'выкл'},
                                    {' '}принуд: {previousForcedText(pair.previous_forced)}
                                  </Typography>
                                </>
                              ) : (
                                <Chip label="новая" color="success" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                size="small"
                                checked={!!enabledMap[key]}
                                onChange={(e) => setEnabledMap((prev) => ({ ...prev, [key]: e.target.checked }))}
                                disabled={saving}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {!visiblePairs.length && (
                        <TableRow>
                          <TableCell colSpan={8}>
                            <Typography color="text.secondary">
                              {pairs.length ? 'Нет пар под фильтр' : 'Новых пар нет'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {tab === 'conflicts' && (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>МП</TableCell>
                      <TableCell>Кабинет</TableCell>
                      <TableCell>Товар МП</TableCell>
                      <TableCell>ШК</TableCell>
                      <TableCell>Причина</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleConflicts.map((row, idx) => (
                      <TableRow key={`${row.marketplace}-${row.account_id}-${row.product_key}-${idx}`}>
                        <TableCell>{MP_LABEL[row.marketplace] || row.marketplace}</TableCell>
                        <TableCell>{row.account_name || row.account_id}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.mp_name || row.product_key}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.product_key}</Typography>
                        </TableCell>
                        <TableCell>{(row.barcodes || []).join(', ') || '—'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.message}</Typography>
                          {row.reason === 'ambiguous_ms' && (
                            <Typography variant="caption" color="text.secondary">
                              {(row.ms_candidates || []).map((c) => textOr(c.ms_name, c.ms_id)).join(' · ')}
                            </Typography>
                          )}
                          {row.reason === 'mp_already_linked' && (
                            <Typography variant="caption" color="text.secondary">
                              Сейчас: {textOr(row.existing_ms_name)}. По ШК: {textOr(row.matched_ms_name)}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!visibleConflicts.length && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography color="text.secondary">Конфликтов нет</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
        {saving && <Box sx={{ mt: 2 }}><LinearProgress /></Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={saving || loading || !selectedCount}>
          {saving ? 'Сохраняем…' : `Сохранить (${selectedCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
